/**
 * The editor's shared vocabulary.
 *
 * `Screens.editor` upstream is one function over one mutable `ED` object (`m08-admin.js:164`).
 * The rebuild splits it across files — the screen, the property panel, the publish gate — so the
 * state that used to be a module-level object has to become an explicit contract. This file is
 * that contract and nothing else: no rendering, no behaviour, so every part can import it without
 * importing the others.
 *
 * The shape is the reference's, field for field, including the ones that look incidental:
 * `propsKey` is upstream's memo key for «has the property panel's STRUCTURE changed, or only its
 * values», and dropping it would rebuild the panel on every pointer move and lose focus mid-edit.
 */
import type { DemoDraft, DemoFieldDefinition } from '../../data/types';

/** A layer as the draft carries it — `makeLayer` / `textLayer` / `qrLayer` in `m02b-seed.js`. */
export interface EditorLayer {
  id: string;
  kind: 'text' | 'static' | 'qr' | 'image';
  x: number;
  y: number;
  width: number;
  height?: number | null;
  zIndex?: number;
  rotation?: number;
  opacity?: number;
  locked?: boolean;
  hidden?: boolean;
  /** text / static */
  fieldKey?: string;
  text?: string;
  /**
   * The same literal in each sealed language, keyed by language.
   *
   * `text` stays the words the release declares itself to be in; this is what a document sealed in
   * another language draws instead. Absent for that language means the release cannot draw this
   * layer in it, which the publish gate refuses by name.
   */
  textLocalized?: Record<string, string>;
  font?: string;
  weight?: number;
  style?: string;
  color?: string;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  fontSizeRatio?: number;
  minFontSizeRatio?: number;
  letterSpacing?: number;
  lineHeight?: number;
  maxLines?: number;
  fitMode?: 'shrink' | 'fixed' | 'wrap';
  textTransform?: 'none' | 'uppercase' | 'lowercase';
  /** image */
  assetRef?: { assetId?: string; sha256?: string } | null;
  imageFit?: string;
  /** both */
  safeAreaId?: string | null;
  [key: string]: unknown;
}

export interface EditorSafeArea {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The working layout: a deep copy of the template's draft that the editor mutates, saved back on
 * «Taslak kaydet». It is never the template's own object, so an unsaved experiment cannot leak
 * into the record the list screen reads.
 */
export interface EditorLayout extends Omit<DemoDraft, 'layers' | 'safeAreas' | 'fieldDefinitions'> {
  layers: EditorLayer[];
  safeAreas: EditorSafeArea[];
  fieldDefinitions: DemoFieldDefinition[];
}

export type EditorTab = 'layer' | 'canvas' | 'fields' | 'template';

export const EDITOR_TABS: [EditorTab, string][] = [
  ['layer', 'Katman'],
  ['canvas', 'Canvas'],
  ['fields', 'Alanlar'],
  ['template', 'Şablon'],
];

/** `ED` — `m08-admin.js:164`, field for field. */
export interface EditorState {
  tplId: string | null;
  layout: EditorLayout | null;
  /** The selected layer's id, or null when the selection was deleted. */
  sel: string | null;
  /** The values the stage previews with; seeded from each field's `sample`. */
  testData: Record<string, string>;
  /** The stage's applied scale, computed by the fit calculation. */
  scale: number;
  zoom: 'fit' | number;
  guides: boolean;
  safe: boolean;
  grid: boolean;
  snap: boolean;
  dirty: boolean;
  /** Structural memo key for the property panel; see the file header. */
  propsKey: string;
  undo: EditorLayout[];
  redo: EditorLayout[];
  aspectLock: boolean;
  tab: EditorTab;
  /**
   * The language being edited — the WORKING language, not a second template.
   *
   * One template, one design, one set of layers and one geometry; this says which language's words
   * and which language's artwork the boxes and the stage are showing. It is presentation state and
   * is deliberately NOT part of the draft: switching it changes nothing that gets saved, and it
   * makes no edit dirty.
   */
  editLocale: 'tr' | 'en';
}

export const INITIAL_EDITOR_STATE: EditorState = {
  tplId: null,
  layout: null,
  sel: null,
  testData: {},
  scale: 1,
  zoom: 'fit',
  guides: true,
  safe: false,
  grid: false,
  snap: false,
  dirty: false,
  propsKey: '',
  undo: [],
  redo: [],
  aspectLock: true,
  tab: 'layer',
  /* Overridden per template by `freshEditorState`, which opens on the language the draft declares. */
  editLocale: 'tr',
};

/** Upstream's snap grid: 2.5% of the canvas, on both axes (`snapLayer`). */
export const SNAP_GRID = 0.025;

/** Arrow-key nudge: 0.25% normally, 1% with Shift (`stageViewport` keydown). */
export const NUDGE_STEP = 0.0025;
export const NUDGE_STEP_SHIFT = 0.01;

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** `snapLayer` — rounds both axes onto the grid, never one. */
export function snapLayer(l: EditorLayer): void {
  l.x = Math.round(l.x / SNAP_GRID) * SNAP_GRID;
  l.y = Math.round(l.y / SNAP_GRID) * SNAP_GRID;
}
